import { useEffect, useState } from 'react'
import { ImageOff } from 'lucide-react'
import { assetDownload } from '@/features/advisory/api'

/**
 * The generated image on a COUNTER_MEME draft (PART IV.2), shown with its disclosure.
 *
 * ## The disclosure is not optional decoration
 *
 * An AI-generated image published without saying so is the single most damaging thing this
 * module could emit, and the harm is not hypothetical — it is the reason the advisor stores a
 * `disclosure` string alongside every asset and returns it from the same call as the URL. So
 * the two are rendered together here, and there is no code path that renders one without the
 * other: the approve button sits underneath, and an operator who cannot see what they are
 * approving should not approve it.
 *
 * ## A failure to load says so
 *
 * If the presigned URL cannot be fetched, this renders "the image could not be loaded" rather
 * than nothing. A draft that silently looks text-only is one somebody approves without ever
 * seeing the picture that goes out attached to it.
 */
export function DraftAsset({ assetId }: { assetId: string }) {
  const [state, setState] = useState<
    { url: string; disclosure: string; model: string } | 'loading' | 'failed'>('loading')

  useEffect(() => {
    let alive = true
    void assetDownload(assetId)
      .then((r) => { if (alive) setState(r) })
      .catch(() => { if (alive) setState('failed') })
    return () => { alive = false }
  }, [assetId])

  if (state === 'loading') {
    return <p className="text-[10px] text-muted-foreground">Loading the attached image…</p>
  }
  if (state === 'failed') {
    return (
      <p className="inline-flex items-center gap-1 rounded border border-amber-500/30
                    bg-amber-500/5 px-2 py-1 text-[10px] text-amber-700 dark:text-amber-400">
        <ImageOff className="size-3" />
        This draft has an image attached and it could not be loaded. Do not approve it until
        you have seen what goes out with it.
      </p>
    )
  }
  return (
    <figure className="mt-1 rounded border border-border p-1.5">
      <img src={state.url} alt="Attached to this draft"
           className="max-h-64 w-auto max-w-full rounded" />
      <figcaption className="mt-1 text-[10px] text-muted-foreground">
        <b className="text-amber-700 dark:text-amber-400">{state.disclosure}</b>
        {' · generated by '}{state.model}. This text goes out with the image, not instead of it.
      </figcaption>
    </figure>
  )
}
